/**
 * NODE REGISTRY - Définition standardisée de tous les types de nœuds
 * 
 * Architecture: Le panneau gauche propose Device, Pause, Code.
 * La sélection du script (Instagram, Gmail, TikTok, Restart…) se fait
 * dans les paramètres du nœud Device une fois le device choisi.
 */

import { 
  Calendar, Clock, Smartphone, Code, Zap, RefreshCw
} from 'lucide-react';

/**
 * Script definitions: each script type groups its own parameters.
 * Used inside the 'device' node after selecting script_type.
 */
export const SCRIPT_DEFINITIONS = {
  'instagram-interact': {
    label: 'Instagram — Interact',
    group: 'Instagram',
    loadFromDB: true,
    targetPurpose: 'interact',
    parameters: [
      { name: 'targets', label: 'Cible spécifique', type: 'target-selector', placeholder: 'username1, username2', helpText: 'Laisser vide ou cocher "Pool" pour utiliser toutes les cibles du compte', targetPurpose: 'interact' },
      { name: 'likes_count', label: 'Likes par profil', type: 'range', min: 0, max: 12 },
      { name: 'stories_count', label: 'Stories par profil', type: 'range', min: 0, max: 30 },
      { name: 'session_length_in_mins_limit', label: 'Durée session (min)', type: 'range', min: 1, max: 480 },
      { name: 'successful_interactions_limit_per_source', label: 'Interactions / source', type: 'range', min: 0, max: 1000 },
      { name: 'follow_percentage', label: 'Follow %', type: 'slider', min: 0, max: 100 },
      { name: 'story_like_percentage', label: 'Story Like %', type: 'slider', min: 0, max: 100 },
      { name: 'comment_percentage', label: 'Comment %', type: 'slider', min: 0, max: 100 },
      { name: 'total_likes_limit', label: 'Limite likes total', type: 'slider', min: 0, max: 1000, step: 5 },
      { name: 'total_story_limit', label: 'Limite stories total', type: 'slider', min: 0, max: 500, step: 5 },
      { name: 'like_target_for_the_day', label: 'Objectif likes / jour', type: 'slider', min: 0, max: 500, step: 5 },
      { name: 'follow_target_for_the_day', label: 'Objectif follows / jour', type: 'slider', min: 0, max: 500, step: 5 },
      { name: 'gemini_analysis', label: 'Analyse Gemini', type: 'toggle', defaultValue: true },
      { name: 'random_tap', label: 'Tap aléatoire (anti-détection)', type: 'toggle', defaultValue: false }
    ]
  },
  'instagram-scrape': {
    label: 'Instagram — Scrape',
    group: 'Instagram',
    loadFromDB: true,
    targetPurpose: 'scrape',
    parameters: [
      { name: 'targets', label: 'Cible spécifique', type: 'target-selector', placeholder: 'username1, username2', helpText: 'Laisser vide ou cocher "Pool" pour utiliser toutes les cibles de scrape du compte', targetPurpose: 'scrape' },
      { name: 'total_scrape_limit', label: 'Limite profils total', type: 'range', min: 10, max: 1000 },
      { name: 'scrape_limit_per_source', label: 'Limite par source', type: 'range', min: 10, max: 1000 },
      { name: 'session_length_in_mins_limit', label: 'Durée session (min)', type: 'slider', min: 1, max: 480 },
      { name: 'gemini_analysis', label: 'Analyse Gemini', type: 'toggle', defaultValue: true },
      { name: 'deep_analysis', label: 'Analyse approfondie (followings)', type: 'toggle', defaultValue: true },
      { name: 'random_tap', label: 'Tap aléatoire (anti-détection)', type: 'toggle', defaultValue: false }
    ]
  },
  'instagram-unfollow': {
    label: 'Instagram — Unfollow',
    group: 'Instagram',
    loadFromDB: true,
    parameters: [
      { name: 'unfollow_type_script', label: "Méthode d'unfollow", type: 'method-selector', options: [
        { value: 'database-global-search', label: 'Recherche (barre)' },
        { value: 'database', label: 'Scroll liste (DB)' },
        { value: 'profile', label: 'Scroll liste (profil)' },
        { value: 'list', label: 'Liste fichier' }
      ]},
      { name: 'following_sort_order', label: 'Tri de la liste', type: 'method-selector', options: [
        { value: 'latest', label: 'Plus récents' },
        { value: 'earliest', label: 'Plus anciens' },
        { value: 'default', label: 'Ordre par défaut' }
      ]},
      { name: 'unfollow_count', label: "Nombre d'unfollows", type: 'range', min: 1, max: 200 },
      { name: 'unfollow_delay', label: 'Délai entre unfollows (sec)', type: 'range', min: 1, max: 60 },
      { name: 'unfollow_limit', label: 'Limite / session', type: 'slider', min: 1, max: 100 },
      { name: 'min_following_hours', label: 'Heures min de suivi', type: 'slider', min: 1, max: 720 },
      { name: 'unfollow_older_than_days', label: 'Plus ancien que (jours)', type: 'slider', min: 1, max: 90 },
      { name: 'session_length_in_mins_limit', label: 'Durée session (min)', type: 'slider', min: 1, max: 480 }
    ]
  },
  'instagram-dm': {
    label: 'Instagram — DM',
    group: 'Instagram',
    loadFromDB: true,
    parameters: [
      { name: 'dm_new_followers', label: 'DM aux nouveaux followers', type: 'toggle' },
      { name: 'dm_to_bot_followed', label: 'DM aux comptes suivis par le bot', type: 'toggle' },
      { name: 'dm_daily_limit', label: 'Limite DMs / jour', type: 'slider', min: 0, max: 100 },
      { name: 'dm_list', label: 'Messages DM (un par ligne)', type: 'textarea', rows: 6, placeholder: 'Ligne 1 du message\nLigne 2 du message\n...' },
      { name: 'session_length_in_mins_limit', label: 'Durée session (min)', type: 'slider', min: 1, max: 480 }
    ]
  },
  'instagram-follower-analysis': {
    label: 'Instagram — Analyse Followers',
    group: 'Instagram',
    loadFromDB: true,
    parameters: [
      { name: 'session_length_in_mins_limit', label: 'Durée session (min)', type: 'slider', min: 1, max: 480 }
    ]
  },
  'instagram-interact-post-likers': {
    label: 'Instagram — Post Likers',
    group: 'Instagram',
    loadFromDB: true,
    parameters: [
      { name: 'post_url', label: 'URL du post', type: 'text', placeholder: 'https://www.instagram.com/p/ABC123/', helpText: 'URL complète du post Instagram dont on veut interagir avec les likers' },
      { name: 'likes_count', label: 'Likes par profil', type: 'range', min: 0, max: 12 },
      { name: 'stories_count', label: 'Stories par profil', type: 'range', min: 0, max: 30 },
      { name: 'session_length_in_mins_limit', label: 'Durée session (min)', type: 'range', min: 1, max: 480 },
      { name: 'successful_interactions_limit_per_source', label: 'Interactions / source', type: 'range', min: 0, max: 1000 },
      { name: 'follow_percentage', label: 'Follow %', type: 'slider', min: 0, max: 100 },
      { name: 'story_like_percentage', label: 'Story Like %', type: 'slider', min: 0, max: 100 },
      { name: 'comment_percentage', label: 'Comment %', type: 'slider', min: 0, max: 100 },
      { name: 'total_likes_limit', label: 'Limite likes total', type: 'slider', min: 0, max: 1000, step: 5 },
      { name: 'total_story_limit', label: 'Limite stories total', type: 'slider', min: 0, max: 500, step: 5 },
      { name: 'gemini_analysis', label: 'Analyse Gemini', type: 'toggle', defaultValue: true },
      { name: 'random_tap', label: 'Tap aléatoire (anti-détection)', type: 'toggle', defaultValue: false }
    ]
  },
  'instagram-interact-list': {
    label: 'Instagram — Interact Liste',
    group: 'Instagram',
    loadFromDB: true,
    parameters: [
      { name: 'likes_count', label: 'Likes par profil', type: 'range', min: 0, max: 12 },
      { name: 'stories_count', label: 'Stories par profil', type: 'range', min: 0, max: 30 },
      { name: 'session_length_in_mins_limit', label: 'Durée session (min)', type: 'range', min: 1, max: 480 },
      { name: 'follow_percentage', label: 'Follow %', type: 'slider', min: 0, max: 100 },
      { name: 'story_like_percentage', label: 'Story Like %', type: 'slider', min: 0, max: 100 },
      { name: 'comment_percentage', label: 'Comment %', type: 'slider', min: 0, max: 100 },
      { name: 'total_likes_limit', label: 'Limite likes total', type: 'slider', min: 0, max: 1000, step: 5 },
      { name: 'total_story_limit', label: 'Limite stories total', type: 'slider', min: 0, max: 500, step: 5 },
      { name: 'gemini_analysis', label: 'Analyse Gemini', type: 'toggle', defaultValue: true },
      { name: 'random_tap', label: 'Tap aléatoire (anti-détection)', type: 'toggle', defaultValue: false }
    ]
  },
  'instagram-detect': {
    label: 'Instagram — Détecter compte',
    group: 'Instagram',
    parameters: []
  },
  'gmail-send': {
    label: 'Gmail — Envoyer',
    group: 'Gmail',
    parameters: [
      { name: 'to', label: 'Destinataire', type: 'text', placeholder: 'email@example.com' },
      { name: 'subject', label: 'Sujet', type: 'text', placeholder: 'Sujet…' },
      { name: 'body', label: 'Corps', type: 'textarea', rows: 6 }
    ]
  },
  'gmail-read': {
    label: 'Gmail — Lire',
    group: 'Gmail',
    parameters: []
  },
  'tiktok-interact': {
    label: 'TikTok — Interact',
    group: 'TikTok',
    parameters: [
      { name: 'targets', label: 'Cibles', type: 'text', placeholder: 'target1, target2' },
      { name: 'likes_count', label: 'Likes max', type: 'number', defaultValue: 30, min: 0 },
      { name: 'max_duration', label: 'Durée max (min)', type: 'number', defaultValue: 30, min: 1 }
    ]
  },
  'restart-connection': {
    label: 'Restart — Connexion device',
    group: 'Utilitaire',
    parameters: [
      { name: 'wait_after', label: 'Attente après restart (sec)', type: 'number', defaultValue: 30, min: 5 }
    ]
  }
};

export const NODE_REGISTRY = {
  'scheduler': {
    type: 'scheduler',
    label: 'Scheduler',
    description: 'Planifier l\'exécution',
    icon: Calendar,
    color: '#14b8a6',
    category: 'trigger',
    parameters: [
      {
        name: 'schedule_type',
        label: 'Type de planification',
        type: 'select',
        defaultValue: 'cron',
        options: [
          { value: 'cron', label: 'Expression Cron' },
          { value: 'datetime', label: 'Date et heure spécifique' },
          { value: 'interval', label: 'Intervalle régulier' }
        ],
        icon: Calendar
      },
      {
        name: 'schedule_cron',
        label: 'Planification',
        type: 'cron-builder',
        defaultValue: '0 9 * * *',
        showWhen: { schedule_type: 'cron' },
        presets: [
          { value: '0 9 * * *', label: 'Tous les jours 9h' },
          { value: '*/30 * * * *', label: 'Toutes les 30 min' },
          { value: '0 */2 * * *', label: 'Toutes les 2h' },
          { value: '0 9 * * 1', label: 'Tous les lundis 9h' },
          { value: '0 0 1 * *', label: '1er du mois minuit' },
          { value: '0 9-17 * * 1-5', label: 'Heures de bureau' }
        ]
      },
      {
        name: 'schedule_datetime',
        label: 'Date et heure',
        type: 'datetime-local',
        showWhen: { schedule_type: 'datetime' }
      },
      {
        name: 'interval_minutes',
        label: 'Intervalle (minutes)',
        type: 'number',
        defaultValue: 30,
        min: 1,
        showWhen: { schedule_type: 'interval' },
        presets: [
          { value: 5, label: '5 min' },
          { value: 15, label: '15 min' },
          { value: 30, label: '30 min' },
          { value: 60, label: '1 heure' },
          { value: 120, label: '2 heures' },
          { value: 360, label: '6 heures' }
        ]
      },
      {
        name: 'workflow_letter',
        label: 'Groupe Workflow (lettre)',
        type: 'workflow-letter-select',
        helpText: 'Lettre du workflow dans instagram_device_configs — filtre les devices disponibles'
      },
      {
        name: 'timezone',
        label: 'Fuseau horaire',
        type: 'select',
        defaultValue: 'Europe/Paris',
        options: [
          { value: 'Europe/Paris', label: 'Europe/Paris (UTC+1)' },
          { value: 'Europe/London', label: 'Europe/London (UTC+0)' },
          { value: 'America/New_York', label: 'America/New_York (UTC-5)' },
          { value: 'America/Los_Angeles', label: 'America/Los_Angeles (UTC-8)' },
          { value: 'Asia/Tokyo', label: 'Asia/Tokyo (UTC+9)' }
        ]
      }
    ]
  },

  'device': {
    type: 'device',
    label: 'Device',
    description: 'Action sur un device',
    icon: Smartphone,
    color: '#10b981',
    category: 'action',
    parameters: [
      {
        name: 'device_id',
        label: 'Device + Compte Instagram',
        type: 'device-account-select',
        required: true,
        icon: Smartphone,
        helpText: 'Choisir le device (le compte IG associé sera affiché)'
      },
      {
        name: 'script_type',
        label: 'Script / Action',
        type: 'select',
        defaultValue: '',
        requiresDevice: true,
        options: Object.entries(SCRIPT_DEFINITIONS).map(([key, def]) => ({
          value: key,
          label: def.label
        })),
        helpText: 'Choisir le script à exécuter sur ce device'
      }
    ]
  },

  'delay': {
    type: 'delay',
    label: 'Pause',
    description: 'Attendre un délai (fixe ou aléatoire)',
    icon: Clock,
    color: '#6366f1',
    category: 'logic',
    parameters: [
      {
        name: 'delay_mode',
        label: 'Mode de délai',
        type: 'select',
        defaultValue: 'random',
        icon: Clock,
        options: [
          { value: 'fixed', label: 'Fixe (durée exacte)' },
          { value: 'random', label: 'Aléatoire (entre min et max)' }
        ],
        helpText: 'Choisir entre un délai fixe ou aléatoire'
      },
      {
        name: 'delay_seconds',
        label: 'Durée du délai (secondes)',
        type: 'number',
        defaultValue: 60,
        min: 1,
        icon: Clock,
        helpText: 'Le workflow attendra exactement X secondes',
        showIf: { field: 'delay_mode', value: 'fixed' }
      },
      {
        name: 'delay_min',
        label: 'Délai minimum (secondes)',
        type: 'number',
        defaultValue: 30,
        min: 1,
        icon: Clock,
        helpText: 'Borne inférieure du délai aléatoire',
        showIf: { field: 'delay_mode', value: 'random' }
      },
      {
        name: 'delay_max',
        label: 'Délai maximum (secondes)',
        type: 'number',
        defaultValue: 120,
        min: 1,
        icon: Clock,
        helpText: 'Borne supérieure du délai aléatoire',
        showIf: { field: 'delay_mode', value: 'random' }
      }
    ]
  },

  'code': {
    type: 'code',
    label: 'Code',
    description: 'Script personnalisé',
    icon: Code,
    color: '#8b5cf6',
    category: 'logic',
    parameters: [
      {
        name: 'language',
        label: 'Langage',
        type: 'select',
        defaultValue: 'javascript',
        options: [
          { value: 'javascript', label: 'JavaScript' },
          { value: 'python', label: 'Python' },
          { value: 'shell', label: 'Shell / Bash' }
        ]
      },
      {
        name: 'script',
        label: 'Code',
        type: 'code-editor',
        rows: 12,
        placeholder: '// Votre code ici…\n// Accédez aux données du nœud précédent via `input`\n// Définissez le résultat via `result`'
      }
    ],
    templates: [
      {
        id: 'random-number',
        label: '🎲 Nombre aléatoire',
        description: 'Génère un nombre aléatoire entre min et max',
        language: 'javascript',
        code: `// Génère un nombre aléatoire entre min et max\nconst min = input.min || 1;\nconst max = input.max || 100;\nresult.random_number = Math.floor(Math.random() * (max - min + 1)) + min;\nresult.min = min;\nresult.max = max;\nconsole.log('Nombre généré:', result.random_number);`
      },
      {
        id: 'random-delay',
        label: '⏱️ Délai aléatoire',
        description: 'Génère un délai aléatoire pour le nœud suivant',
        language: 'javascript',
        code: `// Génère un délai aléatoire entre min et max secondes\nconst minSeconds = input.min_seconds || 30;\nconst maxSeconds = input.max_seconds || 300;\nresult.delay_seconds = Math.floor(Math.random() * (maxSeconds - minSeconds + 1)) + minSeconds;\nconsole.log('Délai généré:', result.delay_seconds, 'secondes');`
      },
      {
        id: 'date-check',
        label: '📅 Vérifier le jour',
        description: 'Vérifie si on est un jour spécifique (weekend, lundi, etc.)',
        language: 'javascript',
        code: `// Vérifie le jour actuel\nconst now = new Date();\nconst day = now.getDay(); // 0=Dim, 1=Lun, ..., 6=Sam\nconst hour = now.getHours();\nresult.day_of_week = ['Dimanche','Lundi','Mardi','Mercredi','Jeudi','Vendredi','Samedi'][day];\nresult.is_weekend = (day === 0 || day === 6);\nresult.is_business_hours = (hour >= 9 && hour < 18 && !result.is_weekend);\nresult.current_hour = hour;\nconsole.log(result.day_of_week, result.is_weekend ? '(weekend)' : '(semaine)');`
      },
      {
        id: 'counter',
        label: '🔢 Compteur',
        description: 'Incrémente un compteur passé en entrée',
        language: 'javascript',
        code: `// Incrémente le compteur du nœud précédent\nresult.counter = (input.counter || 0) + 1;\nresult.message = 'Itération #' + result.counter;\nconsole.log(result.message);`
      },
      {
        id: 'string-transform',
        label: '✏️ Transformation texte',
        description: 'Transforme une chaîne de texte (majuscules, slug, etc.)',
        language: 'javascript',
        code: `// Transforme le texte d'entrée\nconst text = input.text || 'Hello World';\nresult.original = text;\nresult.uppercase = text.toUpperCase();\nresult.lowercase = text.toLowerCase();\nresult.slug = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');\nresult.length = text.length;\nconsole.log('Slug:', result.slug);`
      },
      {
        id: 'json-builder',
        label: '📦 Construire un JSON',
        description: 'Construit un objet JSON à passer au nœud suivant',
        language: 'javascript',
        code: `// Construit un objet de données personnalisé\nresult.username = input.username || 'default_user';\nresult.action = 'interact';\nresult.priority = Math.random() > 0.5 ? 'high' : 'normal';\nresult.timestamp = new Date().toISOString();\nresult.tags = ['auto', 'workflow'];\nconsole.log('Données:', JSON.stringify(result, null, 2));`
      },
      {
        id: 'conditional-output',
        label: '🔀 Sortie conditionnelle',
        description: 'Produit un résultat différent selon une condition',
        language: 'javascript',
        code: `// Sortie conditionnelle basée sur l'entrée\nconst value = input.value || input.counter || 0;\nif (value > 10) {\n  result.branch = 'high';\n  result.delay_seconds = 60;\n  result.message = 'Valeur haute: ' + value;\n} else {\n  result.branch = 'low';\n  result.delay_seconds = 300;\n  result.message = 'Valeur basse: ' + value;\n}\nconsole.log(result.message);`
      },
      {
        id: 'math-operations',
        label: '🧮 Opérations math',
        description: 'Effectue des calculs sur les données d\'entrée',
        language: 'javascript',
        code: `// Opérations mathématiques sur les entrées\nconst values = input.values || [10, 20, 30, 40, 50];\nresult.sum = values.reduce((a, b) => a + b, 0);\nresult.average = result.sum / values.length;\nresult.min = Math.min(...values);\nresult.max = Math.max(...values);\nresult.count = values.length;\nconsole.log('Moyenne:', result.average);`
      }
    ]
  },

  'restarter': {
    type: 'restarter',
    label: 'Restarter',
    description: 'Redémarrer la connexion',
    icon: RefreshCw,
    color: '#f59e0b',
    category: 'action',
    parameters: [
      {
        name: 'device_id',
        label: 'Device Restarter',
        type: 'restarter-device-select',
        required: true,
        icon: RefreshCw,
        helpText: 'Choisir le device restarter (téléphone avec carte SIM)'
      },
      {
        name: 'wait_after',
        label: 'Attente après restart (sec)',
        type: 'number',
        defaultValue: 30,
        min: 5,
        icon: Clock,
        helpText: 'Temps d\'attente après le redémarrage de la connexion'
      }
    ]
  },

  'condition': {
    type: 'condition',
    label: 'Condition',
    description: 'If/Else logique',
    icon: Zap,
    color: '#f59e0b',
    category: 'logic',
    parameters: [
      {
        name: 'condition_type',
        label: 'Type de condition',
        type: 'select',
        defaultValue: 'equals',
        options: [
          { value: 'equals', label: 'Égal à' },
          { value: 'not_equals', label: 'Différent de' },
          { value: 'greater', label: 'Supérieur à' },
          { value: 'less', label: 'Inférieur à' },
          { value: 'contains', label: 'Contient' }
        ]
      },
      {
        name: 'field',
        label: 'Champ à tester',
        type: 'text',
        placeholder: 'nom_du_champ'
      },
      {
        name: 'value',
        label: 'Valeur de comparaison',
        type: 'text',
        placeholder: 'valeur'
      }
    ]
  }
};

/**
 * Récupérer la définition d'un nœud par son type
 * Also supports legacy types (instagram-interact, etc.) by mapping to 'device'
 */
export const getNodeDefinition = (type) => {
  if (NODE_REGISTRY[type]) return NODE_REGISTRY[type];
  // Legacy mapping: old action types → device node
  if (SCRIPT_DEFINITIONS[type]) return NODE_REGISTRY['device'];
  return null;
};

/**
 * Récupérer tous les nœuds disponibles
 */
export const getAllNodeTypes = () => {
  return Object.values(NODE_REGISTRY);
};

/**
 * Récupérer les nœuds par catégorie
 */
export const getNodesByCategory = (category) => {
  return Object.values(NODE_REGISTRY).filter(node => node.category === category);
};

export default NODE_REGISTRY;
